import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  OTHER_REPAIR_ITEM,
  REPAIR_ITEM_OPTIONS,
  normalizeRepairItems,
} from '../../../../constants/repair-items';
import {
  DEFAULT_REPAIR_LOCATIONS,
  IN_HOUSE_REPAIR_LOCATION,
  activeListLabels,
} from '../../../../constants/task-lists';

export interface RepairOrderDialogData {
  selected?: string[];
  repairLocation?: string;
  notes?: string;
  estimate?: string;
  estimateApprovalRequired?: boolean;
  otherText?: string;
  repairLocations?: string[];
}

export interface RepairPopupResult {
  repairItems: string[];
  repairLocation: string;
  notes: string;
  estimate: string;
  estimateApprovalRequired: boolean;
  otherText: string;
}

@Component({
  selector: 'app-repair-order-dialog',
  template: `
    <h2 mat-dialog-title>REPAIR</h2>
    <mat-dialog-content>
      <mat-form-field appearance="fill" class="repair-dialog-field">
        <mat-label>REPAIR LOCATION</mat-label>
        <mat-select [formControl]="repairLocation">
          <mat-option *ngFor="let option of locations" [value]="option">
            {{ option }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <div class="repair-dialog-label">WHAT NEEDS REPAIR?</div>
      <mat-checkbox
        *ngFor="let option of options"
        class="repair-dialog-checkbox"
        [checked]="selected[option]"
        (change)="toggleItem(option, $event.checked)"
      >
        {{ option }}
      </mat-checkbox>
      <mat-form-field
        *ngIf="selected[otherItem]"
        appearance="fill"
        class="repair-dialog-field"
      >
        <mat-label>OTHER</mat-label>
        <textarea matInput rows="3" [formControl]="otherText"></textarea>
      </mat-form-field>
      <mat-form-field appearance="fill" class="repair-dialog-field">
        <mat-label>ESTIMATE</mat-label>
        <input matInput type="text" autocomplete="off" [formControl]="estimate">
        <span matPrefix>$&nbsp;</span>
      </mat-form-field>
      <mat-checkbox
        class="repair-dialog-checkbox"
        [checked]="estimateApprovalRequired.value"
        (change)="estimateApprovalRequired.setValue($event.checked)"
      >
        ESTIMATE / APPROVAL REQUIRED
      </mat-checkbox>
      <mat-form-field appearance="fill" class="repair-dialog-field">
        <mat-label>NOTES</mat-label>
        <textarea matInput rows="3" [formControl]="notes"></textarea>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="cancel()">CANCEL</button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="!canConfirm"
        (click)="confirm()"
      >
        SAVE
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
          .repair-dialog-label {
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.04em;
            margin: 4px 0 8px;
          }
          .repair-dialog-checkbox {
            display: block;
            white-space: normal;
            margin: 4px 0;
          }
          .repair-dialog-field {
            width: 100%;
            margin-top: 8px;
          }
        `,
  ],
})
export class RepairOrderDialogComponent {
  options = REPAIR_ITEM_OPTIONS;
  otherItem = OTHER_REPAIR_ITEM;
  locations: string[] = [];
  selected: { [key: string]: boolean } = {};
  repairLocation = new FormControl(IN_HOUSE_REPAIR_LOCATION);
  notes = new FormControl('');
  estimate = new FormControl('');
  estimateApprovalRequired = new FormControl(false);
  otherText = new FormControl('');

  constructor(
    public dialogRef: MatDialogRef<RepairOrderDialogComponent, RepairPopupResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: RepairOrderDialogData
  ) {
    this.locations = data?.repairLocations?.length
      ? data.repairLocations
      : activeListLabels(undefined, DEFAULT_REPAIR_LOCATIONS);
    if (!this.locations.includes(IN_HOUSE_REPAIR_LOCATION)) {
      this.locations = [IN_HOUSE_REPAIR_LOCATION, ...this.locations];
    }
    const currentLocation = String(data?.repairLocation || IN_HOUSE_REPAIR_LOCATION);
    if (currentLocation && !this.locations.includes(currentLocation)) {
      this.locations = [...this.locations, currentLocation];
    }
    this.repairLocation.setValue(currentLocation || IN_HOUSE_REPAIR_LOCATION);
    this.notes.setValue(String(data?.notes || ''));
    this.estimate.setValue(String(data?.estimate || ''));
    this.estimateApprovalRequired.setValue(!!data?.estimateApprovalRequired);
    this.otherText.setValue(String(data?.otherText || ''));
    normalizeRepairItems(data?.selected || []).forEach((item) => {
      this.selected[item] = true;
    });
  }

  get canConfirm(): boolean {
    return this.options.some((option) => this.selected[option]);
  }

  toggleItem(option: string, checked: boolean): void {
    this.selected[option] = checked;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canConfirm) {
      return;
    }
    const repairItems = this.options.filter((option) => this.selected[option]);
    const otherText = String(this.otherText.value || '').trim();
    const notes = [
      String(this.notes.value || '').trim(),
      repairItems.includes(OTHER_REPAIR_ITEM) ? otherText : '',
    ]
      .filter(Boolean)
      .join(otherText && this.notes.value ? '\n' : '');
    this.dialogRef.close({
      repairItems,
      repairLocation: String(this.repairLocation.value || IN_HOUSE_REPAIR_LOCATION),
      notes,
      estimate: String(this.estimate.value || '').trim(),
      estimateApprovalRequired: !!this.estimateApprovalRequired.value,
      otherText,
    });
  }
}
